import { HttpContext, httpResource } from '@angular/common/http';
import { Service, computed } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../../core/dtos/response-api';
import { HANDLE_HTTP_ERROR_LOCALLY, SKIP_AUTH_TOKEN } from '../../../core/http/http-context-tokens';
import { CanalSicPublicoResponse } from '../models/canal-sic-publico.model';

const RECURSO = `${environment.apiUrl}/sic/publico`;

/**
 * O canal em destaque do e-SIC do órgão, lido **uma vez por carga do portal** e compartilhado pela
 * página de informações (`/sic`) e pelo atendimento presencial (`/sic/presencial`): as duas telas
 * leem o mesmo valor, e nenhum dado — em especial o responsável — pode divergir entre elas.
 * Navegar entre as duas não relê; recarregar a página relê.
 *
 * ⚠️ O portal **não sabe nem envia o órgão**: o back-end o resolve pelo host. Sem token — o canal
 * não depende de quem está logado —, e o erro fica local: falha e `429` caem no mesmo estado vazio
 * de *não publicado*, sem modal.
 */
@Service()
export class CanalSicPublicoService {
  private readonly canalApi = httpResource<ResponseApi<CanalSicPublicoResponse>>(() => ({
    url: `${RECURSO}/canal`,
    context: new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true).set(SKIP_AUTH_TOKEN, true),
  }));

  /** Enquanto a leitura não termina, as telas não exibem nem o canal nem o estado vazio. */
  readonly carregando = computed(() => this.canalApi.isLoading());

  /**
   * O canal publicado, ou `null` quando o órgão não publicou **ou** a leitura falhou — nos dois
   * casos a tela exibe o mesmo estado vazio e não inventa valor.
   */
  readonly canal = computed<CanalSicPublicoResponse | null>(() => {
    if (this.canalApi.error() !== undefined) return null;

    const corpo = this.canalApi.value()?.data;

    return corpo?.publicado ? corpo : null;
  });
}
